import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { StolMessage, StolRoomUsage } from './api';

/**
 * Состояние Стола живёт НАД маршрутами (паттерн PhoneProvider): переходы по
 * админке разговор не обрывают. Открытость и выбранная комната переживают F5;
 * лента сообщений — в памяти, при перезагрузке дотягивается опросом с нуля.
 */

interface StolState {
  open: boolean;
  roomId: number | null;
  draft: string;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  setRoom: (roomId: number | null) => void;
  setDraft: (draft: string) => void;
}

export const useStolStore = create<StolState>()(
  persist(
    (set) => ({
      open: false,
      roomId: null,
      draft: '',
      setOpen: (open) => set({ open }),
      toggle: () => set((s) => ({ open: !s.open })),
      setRoom: (roomId) => set({ roomId }),
      setDraft: (draft) => set({ draft }),
    }),
    {
      name: 'opsmind_stol',
      partialize: (s) => ({ open: s.open, roomId: s.roomId, draft: s.draft }),
    },
  ),
);

/** Лента комнаты вне persist: id → сообщения по возрастанию id. */
interface StolChatState {
  byRoom: Record<number, StolMessage[]>;
  usageByRoom: Record<number, StolRoomUsage | null>;
  appendMessages: (roomId: number, incoming: StolMessage[]) => void;
  setUsage: (roomId: number, usage: StolRoomUsage | null) => void;
  lastId: (roomId: number) => number;
}

export const useStolChatStore = create<StolChatState>()((set, get) => ({
  byRoom: {},
  usageByRoom: {},
  appendMessages: (roomId, incoming) =>
    set((s) => {
      if (incoming.length === 0) {
        return s;
      }
      const existing = s.byRoom[roomId] ?? [];
      const seen = new Set(existing.map((m) => m.id));
      const merged = [...existing];
      for (const m of incoming) {
        if (!seen.has(m.id)) {
          merged.push(m);
        } else {
          // Статус исходящего меняется (queued → delivered) — обновляем на месте.
          const idx = merged.findIndex((x) => x.id === m.id);
          if (idx >= 0) {
            merged[idx] = m;
          }
        }
      }
      merged.sort((a, b) => a.id - b.id);
      return { byRoom: { ...s.byRoom, [roomId]: merged } };
    }),
  setUsage: (roomId, usage) =>
    set((s) => ({ usageByRoom: { ...s.usageByRoom, [roomId]: usage } })),
  lastId: (roomId) => {
    const list = get().byRoom[roomId] ?? [];
    return list.length > 0 ? list[list.length - 1].id : 0;
  },
}));
